import { plannerUrl } from "./AuthApi";

const SCRIPT_ID = "sugar-recaptcha-api";
const READY_CALLBACK = "sugarRecaptchaReady";

declare global {
  interface Window {
    grecaptcha?: {
      ready(cb: () => void): void;
      render(
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
        },
      ): number;
      getResponse(widgetId?: number): string;
      reset(widgetId?: number): void;
    };
    [READY_CALLBACK]?: () => void;
  }
}

let cachedSiteKey: string | null | undefined;
let loadingScript: Promise<void> | null = null;

export async function fetchRecaptchaSiteKey(): Promise<string | null> {
  if (cachedSiteKey !== undefined) return cachedSiteKey;
  try {
    const response = await fetch(plannerUrl("/auth/recaptcha"));
    const data = (await response.json().catch(() => ({}))) as { siteKey?: string };
    cachedSiteKey = data.siteKey?.trim() || null;
  } catch {
    cachedSiteKey = null;
  }
  return cachedSiteKey;
}

export async function loadRecaptchaScript(): Promise<void> {
  if (typeof window === "undefined") return;
  if (window.grecaptcha?.render) {
    await new Promise<void>((resolve) => window.grecaptcha!.ready(() => resolve()));
    return;
  }
  if (!loadingScript) {
    loadingScript = new Promise<void>((resolve, reject) => {
      window[READY_CALLBACK] = () => {
        if (window.grecaptcha?.ready) {
          window.grecaptcha.ready(() => resolve());
          return;
        }
        resolve();
      };
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit&onload=" + READY_CALLBACK;
      script.async = true;
      script.defer = true;
      script.onerror = () => reject(new Error("recaptcha"));
      document.head.appendChild(script);
    }).catch((error) => {
      loadingScript = null;
      throw error;
    });
  }
  await loadingScript;
}

export function renderRecaptchaWidget(host: HTMLElement, siteKey: string): number | null {
  if (!window.grecaptcha?.render) return null;
  host.replaceChildren();
  return window.grecaptcha.render(host, { sitekey: siteKey });
}

export function recaptchaResponse(widgetId: number | null): string {
  if (widgetId == null || !window.grecaptcha?.getResponse) return "";
  return window.grecaptcha.getResponse(widgetId) || "";
}

export function resetRecaptchaWidget(widgetId: number | null): void {
  if (widgetId == null || !window.grecaptcha?.reset) return;
  window.grecaptcha.reset(widgetId);
}
